/**
 * Under the drop list: Add drop / Fill to the end (each new drop takes the
 * next group in turn, or every group when the drops send to every group),
 * then the two quick fills — they rewrite every drop's groups; on the right,
 * "Delete the drops outside the window" while one is (19/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Repeat2, Users } from 'lucide-react';

import { Button, HintDot } from '@/components/ui';

import { fillableSlotCount } from '../../../../../utils/drops/fill-count';
import type { ContainerEditor } from '../../../state/useContainerEditor';
import { SlotFillControls } from '../../fields/SlotFillControls';
import { type OutsideRemovalModel, WINDOW_OPEN } from '../../hooks/use-outside-removal';
import { DeleteOutsideButton } from '../window/DeleteOutsideButton';

interface Props {
  editor: ContainerEditor;
  startMin: number | null;
  /** The end time on the country's clock (the timing mirror's) — null = none. */
  endMin: number | null;
  windowEndMin: number;
  groupCount: number;
  /** The drops' positions in the order the list reads — Alternate follows it. */
  timeOrder: number[];
  /** The drops outside the window (null = none to show, or the container lifts the window). */
  outside?: OutsideRemovalModel | null;
}

export const DropListFooter: FC<Props> = ({ editor, startMin, endMin, windowEndMin, groupCount, timeOrder, outside = null }) => {
  const { draft, slotAdd, slotAddMany, slotsAlternate, slotsAllGroups, slotsRemove, setField } = editor;
  const boundary = endMin ?? windowEndMin;
  const fillCount = fillableSlotCount({
    startMin,
    intervalMin: draft.intervalMin,
    boundaryMin: boundary,
    enabledCount: draft.slots.filter((s) => s.enabled && s.pinnedAtMin === null).length,
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SlotFillControls fillCount={fillCount} boundaryMin={boundary} intervalMin={draft.intervalMin} onAdd={slotAdd} onFill={slotAddMany} />
      {groupCount > 1 && draft.slots.length > 0 ? (
        <>
          <span className="ml-2 text-xs text-muted-foreground">Quick fill</span>
          <Button variant="outline" size="sm" onClick={() => slotsAlternate(timeOrder)}>
            <Repeat2 className="mr-1 h-3.5 w-3.5" /> Alternate groups
          </Button>
          <Button variant="outline" size="sm" onClick={slotsAllGroups}>
            <Users className="mr-1 h-3.5 w-3.5" /> All groups each time
          </Button>
          <HintDot label="Rewrites every drop's groups: one group per drop in turn, or every group on every drop. Then drag and click chips to fine-tune." />
        </>
      ) : null}
      <DeleteOutsideButton model={outside} timeOrder={timeOrder} onRemove={slotsRemove} onStartAtOpen={() => setField('startMode', WINDOW_OPEN)} />
    </div>
  );
};
